import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const VenueGallerySlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const venues = [
    {
      title: "Nightlife Energy",
      category: "Club Scene",
      description: "Vibrant nightlife experiences that capture the essence of urban entertainment",
      image: "/gallery/event-1.jpg",
    },
    {
      title: "Live Performance",
      category: "DJ Sets",
      description: "Professional DJ performances in premium venue settings",
      image: "/gallery/event-2.jpg",
    },
    {
      title: "Creative Studios",
      category: "Production Space",
      description: "Urban art spaces perfect for content creation and live sessions",
      image: "/gallery/event-3.jpg",
    },
    {
      title: "Venue Vibes",
      category: "Social Spaces",
      description: "Modern venues with the right atmosphere for any event",
      image: "/gallery/event-4.jpg",
    },
    {
      title: "Behind the Lens",
      category: "Photography",
      description: "Professional event coverage and creative documentation",
      image: "/gallery/event-5.jpg",
    },
    {
      title: "Community",
      category: "Social Events",
      description: "Building connections through memorable experiences",
      image: "/gallery/event-6.jpg",
    },
    {
      title: "The Decks",
      category: "DJ Culture",
      description: "Where music meets artistry in intimate venue settings",
      image: "/gallery/event-7.jpg",
    },
    {
      title: "Good Times",
      category: "Nightlife",
      description: "Creating unforgettable moments at curated events",
      image: "/gallery/event-8.jpg",
    },
    {
      title: "Live Acts",
      category: "Performance",
      description: "Raw talent and authentic performances that move crowds",
      image: "/gallery/event-9.jpg",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % venues.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [venues.length]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % venues.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + venues.length) % venues.length);
  };

  return (
    <section className="py-20 bg-black relative overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-block bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm text-gray-300 mb-4">
            Our Network
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            The Culture We
            <span className="text-gray-400"> Represent</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            From underground clubs to premium venues, we connect agencies with Africa's most vibrant creative scenes and talent.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Main Slider */}
          <div className="relative h-[500px] rounded-3xl overflow-hidden">
            {venues.map((venue, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                  index === currentIndex
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-95"
                }`}
              >
                <img 
                  src={venue.image} 
                  alt={venue.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                
                {/* Content */}
                <div className="absolute inset-0 flex items-end p-8">
                  <div className="space-y-3">
                    <span className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-xs text-white">
                      {venue.category}
                    </span>
                    <h3 className="text-3xl font-bold text-white">{venue.title}</h3>
                    <p className="text-gray-300 max-w-md">{venue.description}</p>
                  </div>
                </div>
              </div>
            ))}

            {/* Navigation Buttons */}
            <Button
              variant="ghost"
              size="icon"
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full h-12 w-12"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full h-12 w-12"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {venues.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? "w-8 bg-white"
                    : "w-2 bg-white/30 hover:bg-white/50"
                }`}
              />
            ))}
          </div>

          {/* Thumbnails */}
          <div className="grid grid-cols-3 md:grid-cols-9 gap-2 mt-8">
            {venues.map((venue, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`relative aspect-square rounded-xl overflow-hidden transition-all duration-300 ${
                  index === currentIndex
                    ? "ring-2 ring-white ring-offset-2 ring-offset-black"
                    : "opacity-50 hover:opacity-80"
                }`}
              >
                <img 
                  src={venue.image} 
                  alt={venue.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default VenueGallerySlider;
